/**
 * "Open the ข้อความ dock for this patient, here, now."
 *
 * The toast and the inbox list used to link to `/patient/:id/...?chat=1` in a new
 * tab. That is the wrong move for a chat: reading a two-line message cost a whole
 * page load, and the front desk — who already keeps four patient tabs open —
 * collected one more tab per message. The dock is self-contained
 * (`PatientChatDock` takes a patientId), so it can just open over whatever you are
 * already looking at.
 *
 * A window event rather than a context: the senders (ChatToaster,
 * ChatInboxTrigger) and the host are mounted in different layouts, and this keeps
 * them from having to share a provider that spans both.
 */
const EVT = 'ss:open-chat-dock'

/** @param {{patientId: number|string, patientName?: string}} req */
export function openChatDock(req) {
  if (!req?.patientId) return
  window.dispatchEvent(new CustomEvent(EVT, { detail: req }))
}

export function onOpenChatDock(handler) {
  const h = (e) => handler(e.detail)
  window.addEventListener(EVT, h)
  return () => window.removeEventListener(EVT, h)
}

/* ── who has a dock open ──────────────────────────────────────────────────── */
/**
 * The toast is a *reach* mechanism: it exists so a message about patient A finds
 * you while you are in patient B. If the thread is already open on screen, the
 * message has arrived — the dock appends it live — and the toast is pure noise
 * that covers the input you are typing into.
 *
 * A Set rather than a single id, because two docks can be mounted at once (the
 * patient page owns one, ChatDockHost another for a different patient), and
 * whichever unmounts first must not clear the other's flag.
 */
const openDocks = new Set()

export function markChatDockOpen(patientId, isOpen) {
  if (patientId == null) return
  const key = String(patientId)
  if (isOpen) openDocks.add(key)
  else openDocks.delete(key)
}

export function isChatDockOpenFor(patientId) {
  return patientId != null && openDocks.has(String(patientId))
}

/** Is ANY chat dock open? ChatDockHost is mounted above every route (Layout.jsx), so a page
 *  underneath it can be reached by a global key handler while the operator is reading a
 *  chat — focus sits on a message bubble, not an input, so a tag check would not catch it.
 *  Used by ClaimImportRun's ←/→ navigation. */
export const isAnyChatDockOpen = () => openDocks.size > 0

/* ── inbound message bus ──────────────────────────────────────────────────── */
/**
 * `chat_inbox/{user_id}` has ONE subscriber in the app — ChatToaster — which
 * re-broadcasts what it receives on this bus, and any open dock refetches from it.
 *
 * It was built this way to dodge a channel collision: `destroy()` sends a
 * channel-wide Leave, so the first of two same-name subscribers to unmount used
 * to deafen the other. `lib/realtime.js` now shares one channel per name, so a
 * second subscriber would be safe — the bus stays because it works, not because
 * it is required.
 *
 * The dock also watches `live` (the patient_update event) —
 * this is the path that is actually proven to arrive at the recipient, since it
 * is the same one that has been raising the toasts.
 */
const MSG = 'ss:chat-message'

export function emitChatMessage(payload) {
  if (!payload) return
  window.dispatchEvent(new CustomEvent(MSG, { detail: payload }))
}

export function onChatMessage(handler) {
  const h = (e) => handler(e.detail)
  window.addEventListener(MSG, h)
  return () => window.removeEventListener(MSG, h)
}
